import withParams from "@src/components/widgets/HOC/withParams";
import React, { useCallback } from "react";
import { WidgetTemplateComponentType } from "@src/interface/Widget";
import { ILandingStandardWidgetInnovationsParams } from "../interface";
import LandingInnovation from "design-system/src/Components/WidgetPart/LandingInnovation/LandingInnovation";
import useProductList, {
  InnovationProductAdditionalData,
} from "@src/components/widgets/hooks/useProductList";
import { useTranslation } from "@src/hooks/useTranslation";
import InnovationProduct, {
  IInnovationProductProps,
} from "design-system/src/Components/WidgetPart/LandingInnovation/InnovationProduct/InnovationProduct";
import useCtaAction from "@src/components/widgets/hooks/useCtaAction";
import { usePushAppsModal } from "@src/lib/WidgetPushApp/WidgetPushApp";
import {
  InnovationAmbianceCoverMedia,
  InnovationProductTileVisual,
} from "design-system/src/Components/WidgetPart/LandingStandardMedia";
import { resolveDualResponsiveMedia } from "design-system/src/Components/WidgetPart/LandingStandardMedia/resolveResponsiveMedia";
import { renderLandingStandardSushiShopVideo } from "../renderLandingStandardSushiShopVideo";

const ProductItemRender = (
  props: IInnovationProductProps<InnovationProductAdditionalData>
) => {
  const {
    product: flatProduct,
    onClick: defaultOnClick,
    ...rest
  } = props;
  const { id_product, actionType, link } = flatProduct;
  const { t } = useTranslation(["landing"]);
  const { generateClick, getSubmitLabel } = useCtaAction(id_product || "");
  const attachPushApps = flatProduct.attachPushApps;
  const { openModal, canShowPushApps } = usePushAppsModal();

  const onClick = generateClick(
    actionType && actionType === "open_link"
      ? { type: "open_link", link: link || "#" }
      : { type: actionType || "product_card" }
  );

  const handleClick = () => {
    if (attachPushApps && canShowPushApps()) {
      openModal();
    } else {
      onClick?.();
    }
  };
  return (
    <InnovationProduct
      {...rest}
      ctaLabel={getSubmitLabel(false, t("Je commande", "landing"))}
      product={flatProduct}
      onClick={handleClick}
      renderCustomVisual={({
        aspectRatio,
        chefDesktopVideoHoverZoneRef,
      }) => (
        <InnovationProductTileVisual
          desktop={flatProduct.visualDesktop}
          mobile={flatProduct.visualMobile}
          fallbackImageId={flatProduct.imageId}
          alt={flatProduct.title}
          aspectRatio={aspectRatio}
          renderVideo={renderLandingStandardSushiShopVideo}
          videoHoverPlayContainerRef={chefDesktopVideoHoverZoneRef}
        />
      )}
    />
  );
};

const LandingStandardInnovations: WidgetTemplateComponentType =
  withParams<ILandingStandardWidgetInnovationsParams>((props) => {
    const { p, params } = props;
    const { t } = useTranslation(["UI"]);
    const { prepareInnovationProductList } = useProductList(params);
    const style_template: ILandingStandardWidgetInnovationsParams["style_template"] =
      p("style_template");

    const rawParams = params as unknown as Record<string, unknown>;
    const ambianceDual = resolveDualResponsiveMedia(rawParams, "ambiance_visual", {
      desktopImageKey: "ambiance_image",
      mobileImageKey: "ambiance_image",
    });
    const ambianceHasMedia =
      ambianceDual.desktop !== undefined || ambianceDual.mobile !== undefined;

    const renderProduct = useCallback(
      (data: IInnovationProductProps, index: number) => {
        return (
          <ProductItemRender
            key={data.product.id_product + "-" + index}
            {...data}
          />
        );
      },
      []
    );
    return (
      <LandingInnovation<InnovationProductAdditionalData>
        title={p("title") + ""}
        highlightedTitle={p("subtitle")}
        description={p("description")}
        coverImageId={p("ambiance_image")}
        coverAmbianceSlot={
          style_template === "ambiance-visual" && ambianceHasMedia ? (
            <InnovationAmbianceCoverMedia
              dual={ambianceDual}
              coverRatio={3 / 1}
              renderVideo={renderLandingStandardSushiShopVideo}
            />
          ) : undefined
        }
        products={prepareInnovationProductList(7, style_template)}
        variant={style_template}
        showLessLabel={t("Voir moins", "UI")}
        showMoreLabel={t("Voir plus", "UI")}
        renderProduct={renderProduct}
      />
    );
  });

export default LandingStandardInnovations;
